import { type ElementType } from 'react';
import { Card, type CardShadow } from '../Card';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { cn } from '@/utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * CampaignCard component props.
 *
 * Sealed — `className` is not exposed. CampaignCard is a fixed-layout design
 * system component. If a new visual variant is needed, add a prop here rather
 * than reaching for a className override.
 */
export interface CampaignCardProps {
  /** URL of the product image. Displayed full-width at the top of the card. */
  imageUrl?: string;
  /** Alt text for the product image. Defaults to an empty string. */
  imageAlt?: string;
  /** Brand / campaign label. Rendered in body/small/bodylight. e.g. "Nespresso collab" */
  brandLabel: string;
  /** Product display name. Rendered in body/large/bold as h3. */
  productName: string;
  /** Optional tags displayed as pills below the product name. e.g. ["We Ship", "Instagram Post"] */
  tags?: ReadonlyArray<string>;
  /** Optional bold number shown at the start of the badge (e.g. 10 in "10 hrs left"). */
  badgeCount?: number;
  /** Optional badge label text shown after badgeCount, or standalone (e.g. "hrs left", "Completed"). */
  badgeLabel?: string;
  /** Controls the badge colour. */
  badgeVariant?: 'success' | 'warning';
  /** CTA button label. Defaults to "View campaign". */
  ctaLabel?: string;
  /** CTA button variant. Defaults to "primary". */
  ctaVariant?: 'primary' | 'secondary';
  /** When true, the CTA button is disabled (e.g. expired campaigns). */
  ctaDisabled?: boolean;
  /** Called when the user taps the CTA button. */
  onViewProposal: () => void;
  /**
   * Shadow depth forwarded to the inner Card.
   * @see CardShadow
   */
  shadow?: CardShadow;
  /** Root HTML element for the inner Card. @default "div" */
  as?: ElementType;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer CampaignCard component.
 *
 * A vertical-layout card presenting a campaign/product opportunity. The product
 * image spans the full card width at the top; the content pane below holds the
 * brand label, product name, optional tags, and a "View campaign" CTA button.
 *
 * ### Layout
 * - **Top** — square aspect ratio (`aspect-square`), `object-contain` on `bg-page` background.
 *   Scales proportionally with the card/column width. No fixed dimensions — works at any grid size.
 * - **Bottom** — `p-4 flex flex-col gap-3`; brand label, product name (h3), tags row, button
 *
 * ### Sealed component
 * `className` is not exposed. Layout (width, margin, grid placement) belongs on
 * the parent container. If a new visual state is needed, add a prop.
 *
 * @example
 * <CampaignCard
 *   imageUrl="/images/nespresso.png"
 *   brandLabel="Nespresso collab"
 *   productName="Nespresso Vertuo Pop Coffee Machine"
 *   tags={["We ship", "Post on Instagram", "$350"]}
 *   onViewProposal={() => navigate({ to: '/campaign/$id', params: { id: '1' } })}
 * />
 */
export function CampaignCard({
  imageUrl,
  imageAlt = '',
  brandLabel,
  productName,
  tags,
  badgeCount,
  badgeLabel,
  badgeVariant = 'warning',
  ctaLabel = 'View campaign',
  ctaVariant = 'primary',
  ctaDisabled = false,
  onViewProposal,
  shadow,
  as,
}: CampaignCardProps) {
  return (
    <Card shadow={shadow} as={as}>
      {/* ── Product image — square aspect ratio, contained, scales with card width ── */}
      {imageUrl && (
        <div className="w-full aspect-square bg-surface overflow-hidden relative">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="w-full h-full object-contain"
            onError={(e) => {
              e.currentTarget.src = '/assets/images/placeholder.svg';
            }}
          />
          {(badgeCount !== undefined || badgeLabel) && (
            <span
              data-testid="campaign-badge"
              className={cn(
                'absolute top-2 right-2 rounded-rounded px-4 py-1 border inline-flex items-center gap-1',
                badgeVariant === 'success'
                  ? 'bg-success border-success'
                  : 'bg-warning border-warning'
              )}
            >
              {badgeCount !== undefined && (
                <Typography
                  variant="body"
                  size="xs"
                  color={badgeVariant === 'success' ? 'success' : 'warning'}
                  decoration={{ bold: true }}
                  as="span"
                >
                  {badgeCount}
                </Typography>
              )}
              {badgeLabel && (
                <Typography
                  variant="body"
                  size="xs"
                  color={badgeVariant === 'success' ? 'success' : 'warning'}
                  as="span"
                >
                  {badgeLabel}
                </Typography>
              )}
            </span>
          )}
        </div>
      )}

      {/* ── Divider between image and content ─────────────────────────── */}
      {imageUrl && <Divider />}

      {/* ── Content pane ──────────────────────────────────────────────────── */}
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          {/* Brand label */}
          <Typography variant="body" size="small" color="bodylighter">
            {brandLabel}
          </Typography>

          {/* Product name */}
          <Typography variant="header" size="h1" as="h1">
            {productName}
          </Typography>
        </div>

        {/* Tags + CTA — mt-auto keeps both pinned to the bottom of the card */}
        <div className="mt-auto flex flex-col gap-3">
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-interactive px-3 py-1 inline-flex items-center"
                >
                  <Typography variant="body" size="xs" color="interactive" as="span">
                    {tag}
                  </Typography>
                </span>
              ))}
            </div>
          )}

          <Button fullWidth variant={ctaVariant} onClick={onViewProposal} disabled={ctaDisabled}>
            {ctaLabel}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default CampaignCard;
